/**
 * Deletion Audit Log — NTN-staff-only view of every permanent deletion event.
 *
 * Section 1 of the Admin Controls spec: applicant name, id, property,
 * deleted-by, date, and reason. Includes free-text search and pagination.
 */
import { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/AppShell";
import { apiClient, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Trash2, FileX2, History } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dateFormat";

function fmt(iso) {
  if (!iso) return "—";
  return fmtDateTime(iso);
}

export default function DeletionLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admin/deletion-audit", {
        params: { q, limit, offset },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
    }
  }, [q, offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="mb-8 ntn-fade-up">
        <div className="text-[10px] tracking-widest uppercase font-semibold text-slate-500 mb-2 flex items-center gap-2">
          <History className="h-3.5 w-3.5" /> Staff console · Audit
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-slate-900" data-testid="deletion-log-title">Deletion Log</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Every permanent deletion of an applicant or document is recorded here. This audit log is preserved even after the underlying records are removed.
        </p>
      </div>

      <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200">
          <Search className="h-4 w-4 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="Search applicant, property, deleter, reason…"
            className="border-0 shadow-none focus-visible:ring-0 h-9 bg-transparent"
            data-testid="deletion-log-search"
          />
          <span className="text-[11px] font-mono text-slate-500">{total} event{total !== 1 ? "s" : ""}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] tracking-widest uppercase font-semibold text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">When</th>
                <th className="text-left px-4 py-2.5">Kind</th>
                <th className="text-left px-4 py-2.5">Applicant / File</th>
                <th className="text-left px-4 py-2.5">Property</th>
                <th className="text-left px-4 py-2.5">Deleted by</th>
                <th className="text-left px-4 py-2.5">Reason</th>
                <th className="text-right px-4 py-2.5">Counts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm">No deletions logged yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`deletion-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{fmt(r.deleted_at)}</td>
                  <td className="px-4 py-3">
                    {r.kind === "file" ? (
                      <Badge variant="outline" className="text-[10px] uppercase font-mono border-amber-200 bg-amber-50 text-amber-700">
                        <FileX2 className="h-3 w-3 mr-1" /> File
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] uppercase font-mono border-red-200 bg-red-50 text-red-700">
                        <Trash2 className="h-3 w-3 mr-1" /> Applicant
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <div className="text-slate-900 truncate">{r.applicant_name || r.file_name || "—"}</div>
                    <div className="text-[11px] font-mono text-slate-500 truncate">
                      {r.applicant_email || r.screening_id || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 truncate max-w-[260px] text-slate-700">{r.property_address || "—"}</td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <div className="text-slate-900 truncate">{r.deleted_by_name}</div>
                    <div className="text-[11px] font-mono text-slate-500 truncate">{r.deleted_by_email}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">
                    <div className="text-slate-700 line-clamp-2 whitespace-pre-wrap break-words">{r.reason}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[11px] text-slate-600 whitespace-nowrap">
                    {r.cascade_counts ? (
                      <>
                        {r.cascade_counts.files}f · {r.cascade_counts.notes}n · {r.cascade_counts.activity}a · {r.cascade_counts.messages}m
                      </>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-[11px] font-mono text-slate-500">
            {offset + 1} – {Math.min(offset + rows.length, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    data-testid="deletion-log-prev">Prev</Button>
            <Button size="sm" variant="outline" disabled={offset + limit >= total}
                    onClick={() => setOffset(offset + limit)}
                    data-testid="deletion-log-next">Next</Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
